import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';

const GRID_SIZE = 20;
const CELL_SIZE = 20;
const INITIAL_SNAKE = [{ x: 10, y: 10 }];
const INITIAL_DIRECTION = { x: 0, y: 0 };

const SnakeGame = () => {
    const { theme } = useTheme();
    const { t } = useLanguage();
    const [snake, setSnake] = useState(INITIAL_SNAKE);
    const [food, setFood] = useState({ x: 15, y: 15 });
    const [direction, setDirection] = useState(INITIAL_DIRECTION);
    const [gameOver, setGameOver] = useState(false);
    const [score, setScore] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    const generateFood = useCallback(() => {
        return {
            x: Math.floor(Math.random() * GRID_SIZE),
            y: Math.floor(Math.random() * GRID_SIZE),
        };
    }, []);

    const resetGame = () => {
        setSnake(INITIAL_SNAKE);
        setFood(generateFood());
        setDirection(INITIAL_DIRECTION);
        setGameOver(false);
        setScore(0);
        setIsPlaying(false);
    };

    const startGame = () => {
        setIsPlaying(true);
        setDirection({ x: 1, y: 0 });
    };

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (!isPlaying) return;

            switch (e.key) {
                case 'ArrowUp':
                    e.preventDefault();
                    if (direction.y === 0) setDirection({ x: 0, y: -1 });
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    if (direction.y === 0) setDirection({ x: 0, y: 1 });
                    break;
                case 'ArrowLeft':
                    e.preventDefault();
                    if (direction.x === 0) setDirection({ x: -1, y: 0 });
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    if (direction.x === 0) setDirection({ x: 1, y: 0 });
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [direction, isPlaying]);

    useEffect(() => {
        if (!isPlaying || gameOver) return;

        const moveSnake = setInterval(() => {
            setSnake((prevSnake) => {
                const newHead = {
                    x: prevSnake[0].x + direction.x,
                    y: prevSnake[0].y + direction.y,
                };

                // Check collision with walls
                if (
                    newHead.x < 0 ||
                    newHead.x >= GRID_SIZE ||
                    newHead.y < 0 ||
                    newHead.y >= GRID_SIZE
                ) {
                    setGameOver(true);
                    return prevSnake;
                }

                // Check collision with self
                if (prevSnake.some((segment) => segment.x === newHead.x && segment.y === newHead.y)) {
                    setGameOver(true);
                    return prevSnake;
                }

                const newSnake = [newHead, ...prevSnake];

                // Check collision with food
                if (newHead.x === food.x && newHead.y === food.y) {
                    setScore((prev) => prev + 1);
                    setFood(generateFood());
                } else {
                    newSnake.pop();
                }

                return newSnake;
            });
        }, 150);

        return () => clearInterval(moveSnake);
    }, [direction, food, gameOver, generateFood, isPlaying]);

    const borderColor = theme === 'dark' ? 'border-gray-700' : 'border-gray-200';
    const snakeColor = theme === 'dark' ? 'bg-white' : 'bg-charcoal';
    const foodColor = 'bg-swiss-red';
    const gridBg = theme === 'dark' ? 'bg-dark-bg' : 'bg-off-white';

    return (
        <div className={`flex flex-col items-center p-6 rounded-xl border ${borderColor} ${theme === 'dark' ? 'bg-charcoal' : 'bg-white'}`}>
            <div className="mb-4 flex justify-between w-full max-w-[400px]">
                <span className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-charcoal'}`}>
                    {t('snake.score')}: {score}
                </span>
            </div>

            <div
                className={`relative ${gridBg} border ${borderColor}`}
                style={{
                    width: GRID_SIZE * CELL_SIZE,
                    height: GRID_SIZE * CELL_SIZE
                }}
            >
                {!isPlaying && !gameOver && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                        <button
                            onClick={startGame}
                            className="bg-white text-charcoal px-6 py-2 font-bold hover:bg-gray-100 transition-colors"
                        >
                            {t('snake.start')}
                        </button>
                    </div>
                )}

                {gameOver && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 z-10 text-white">
                        <p className="text-xl font-bold mb-4">{t('snake.gameOver')}</p>
                        <button
                            onClick={resetGame}
                            className="bg-white text-charcoal px-6 py-2 font-bold hover:bg-gray-100 transition-colors"
                        >
                            {t('snake.playAgain')}
                        </button>
                    </div>
                )}

                {snake.map((segment, index) => (
                    <div
                        key={index}
                        className={`absolute ${snakeColor}`}
                        style={{
                            left: segment.x * CELL_SIZE,
                            top: segment.y * CELL_SIZE,
                            width: CELL_SIZE - 2,
                            height: CELL_SIZE - 2,
                        }}
                    />
                ))}
                <div
                    className={`absolute ${foodColor} rounded-full`}
                    style={{
                        left: food.x * CELL_SIZE,
                        top: food.y * CELL_SIZE,
                        width: CELL_SIZE - 2,
                        height: CELL_SIZE - 2,
                    }}
                />
            </div>

            <p className={`mt-4 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Use arrow keys to move / Usa las flechas para moverte
            </p>
        </div>
    );
};

export default SnakeGame;
